import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { DocumentData, ExtractedData, JsonContent, OnboardingDocument, PdfDetailsResponse, UnclassifiedDocument } from '../types';
import config from 'config';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { message } from 'antd';
import { ensureJsonContentMetadata } from '../defaultMetadataByDocumentType';
import { fixJSONDates, sliceEmailBodyInContent } from './contants';
import { extractAssessmentReportItems, extractAssessmentReportZipCode, rebuildSectionsFromFormData, resolveJsonIdForTab } from '../utils';

const DocumentProcessingContext = createContext(null);

export const useDocumentProcessing = () => {
  return useContext(DocumentProcessingContext);
};

interface DocumentProcessingProviderProps {
  children: React.ReactNode;
}

export const DocumentProcessingProvider = ({ children }: DocumentProcessingProviderProps) => {
  const intl = useIntl();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [documents, setDocuments] = useState<DocumentData[]>([]);
  const [showDocumentUploadModal, setShowDocumentUploadModal] = useState(false);
  const [openSendEmailModal, setOpenSendEmailModal] = useState(false);
  const [emailFrom, setEmailFrom] = useState<string | null>(null);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [showReextractModal, setShowReextractModal] = useState(false);
  const [processedDocuments, setProcessedDocuments] = useState<DocumentData[]>([]);
  const token = useSelector<any>(state => state?.petrus?.entities?.tokens?.accessToken?.token);
  const commonHeaders = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    'Accept-Language': intl.locale,
  };
  const [loading, setLoading] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentData | null>(null);
  const [deletedDocuments, setDeletedDocuments] = useState<DocumentData[]>([]);
  const [extractedData, setExtractedData] = useState<ExtractedData | null>(null);
  const [pdfDetails, setPdfDetails] = useState<PdfDetailsResponse | null>(null);
  const [formData, setFormData] = useState({});
  const [filteredTableData, setFilteredTableData] = useState<DocumentData[]>([]);
  const [filterOptions, setFilterOptions] = useState<{ [key: string]: string }>({});
  const [tabKey, setTabKey] = useState('1');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = React.useState(false);
  const [duplicateDocument, setDuplicateDocument] = React.useState<DocumentData | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [totalCount, setTotalCount] = useState(0);
  const [unclassifiedDocuments, setUnclassifiedDocuments] = useState<UnclassifiedDocument[]>([]);
  const [unclassifiedPdfUrl, setUnclassifiedPdfUrl] = useState<string | null>(null);
  const [onboardingDocuments, setOnboardingDocuments] = useState<OnboardingDocument[]>([]);
  const [onboardingPdfUrl, setOnboardingPdfUrl] = useState<string | null>(null);
  const [landingPageRefreshToken, setLandingPageRefreshToken] = useState(0);

  const getDocumentTypeBaseOnTabKey = (key: string) => {
    switch (key) {
      case '1':
        return 'extractions';
      case '2':
        return 'processing_completed';
      case '3':
        return 'deleted';
      case '7':
        return 'ai_submitted';
      case '8':
        return 'investigation';
      default:
        return 'all';
    }
  };

  const handleDeleteDocument = async data => {
    try {
      await fetch(`${config.api.base}${config.api.documentProcessing}/${data.jsonId}`, {
        method: 'DELETE',
        headers: commonHeaders,
      });

      await fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey), filterOptions);
      setShowDeleteModal(false);
      setShowDuplicateModal(false);
      setDetailModalVisible(false);
      
    } catch (error) {}
  };

  const revertToPending = async data => {
    try {
      await fetch(`${config.api.base}${config.api.documentProcessing}/revert/processing_completed/json`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ path: data.jsonId }),
      });

      await fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey), filterOptions);
      setShowRevertModal(false);
    } catch (error) {}
  };

  const restoreDeletedToPending = async data => {
    try {
      await fetch(`${config.api.base}${config.api.documentProcessing}/revert/deleted/json`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ path: data.jsonId }),
      });

      await fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey), filterOptions);
      setShowRevertModal(false);
    } catch (error) {}
  };

  const submitAiSubmittedToPending = async data => {
    try {
      await fetch(`${config.api.base}${config.api.documentProcessing}/revert/ai_submitted/json`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ path: data.jsonId }),
      });

      await fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey), filterOptions);
      setShowRevertModal(false);
      setDetailModalVisible(false);
    } catch (error) {}
  };

  const buildReextractPayload = (data: DocumentData) => {
    const jsonContent = data.jsonContent as unknown as Record<string, unknown> | undefined;
    return {
      DocumentType: jsonContent?.DocumentType,
      GStoreId: jsonContent?.GStoreId ?? data.pdfPath,
      DocumentId: String(jsonContent?.DocumentId ?? data.id),
      collection: getDocumentTypeBaseOnTabKey(tabKey),
    };
  };

  const resendForExtraction = async (data: DocumentData) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${config.api.base}${config.api.documentProcessing}/reextract`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify(buildReextractPayload(data)),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to resend for extraction');
      }

      message.success(intl.formatMessage({ id: 'document.processing.reextract.success' }));
      await fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey), filterOptions);
      setShowReextractModal(false);
      setDetailModalVisible(false);
    } catch (error: any) {
      message.error(error?.message || intl.formatMessage({ id: 'document.processing.reextract.error' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const sendEmail = async data => {
    try {
      await fetch(`${config.api.base}/v1/emails/send`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify(data),
      });
    } catch (error) {}
    setOpenSendEmailModal(false);
  };

  const mapTypeToBackend = (type: string, environment: string = 'Prod') => {
    const typeMap = {
      'invoice': `Customer_Invoice_${environment}`,
      'assessment report': `Assessment_Report_${environment}`,
      'contract': `Customer_Contract_${environment}`,
      'document': `Customer_Document_${environment}`,
      'quotation': `Customer_Quotation_${environment}`,
      'price adjustment': `Price_Adjustment_${environment}`,
    };
    return typeMap[type] || type;
  };

  const fetchDocuments = async (documentType = 'extractions', options = {}, page = 1, pageSize = 10, isOnboarding = false) => {
    setLoading(true);
    try {
      // Build query parameters
      const offset = (page - 1) * pageSize;
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: offset.toString(),
      });

      if (isOnboarding) {
        params.append('isOnboarding', 'true');
      }

      // Add filters if provided
      const filterKeys = Object.keys(options).filter(key => options[key]);
      if (filterKeys.length > 0) {
        const filters: Record<string, unknown> = {};
        filterKeys.forEach(key => {
          // Map the type filter value to backend format if it's a type filter
          if (key === 'type') {
            filters[key] = mapTypeToBackend(options[key]);
          } else if (key === 'gstoreId') {
            filters.GStoreId = options[key];
          } else {
            filters[key] = options[key];
          }
        });
        params.append('filters', JSON.stringify(filters));
      }

      const response = await fetch(
        `${config.api.base}${config.api.documentProcessing}/${documentType}?${params.toString()}`,
        {
          method: 'GET',
          headers: commonHeaders,
        }
      );
      const result = await response.json();

      // Handle API response structure: { data: [], pagination: { total, limit, offset, hasMore } }
      const data = (result.data || result || [])
        .map((doc: DocumentData) => ({
          ...doc,
          gstoreId: doc.gstoreId ?? (doc.jsonContent as JsonContent | undefined)?.GStoreId ?? '',
        }))
        // Landing page assessment reports live in the Landing Page Documents tab, not here.
        .filter((doc: DocumentData) => documentType !== 'extractions' || doc.type !== 'Landing_Page_Assessment_Report');
      const total = result.pagination?.total || data.length;

      setFilteredTableData(data);
      setTotalCount(total);
      setPagination({ current: page, pageSize, total });

      if (documentType === 'extractions' || documentType === 'ai_submitted' || documentType === 'investigation') {
        setDocuments(data);
      }

      if (documentType === 'processing_completed') {
        setProcessedDocuments(data);
      }
      if (documentType === 'deleted') {
        setDeletedDocuments(data);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const submitToSalesforce = useCallback(async (documentId: string, processedData: ExtractedData) => {
    try {
      // For now, simulate the API call
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
      // Update the document status in the list
      setDocuments(prev => prev.map(doc => (doc.id === documentId ? { ...doc, status: 'completed' as const } : doc)));
    } catch (error) {
      throw error;
    }
  }, []);

  const updateExtractedData = useCallback(async (documentId: string, data: ExtractedData) => {
    try {
      // For now, simulate the API call
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API delay
      setExtractedData(data);
    } catch (error) {
      throw error;
    }
  }, []);

  const getPdfUrl = async (doc: DocumentData) => {
    setLoading(true);
    try {
      const response = await fetch(`${config.api.base}${config.api.documentProcessing}/pdf`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({
          fileId: doc.pdfPath,
          jsonId: resolveJsonIdForTab(doc.jsonId, doc.pdfPath, tabKey),
        }),
      });
      const data = await response.json();

      // Check if response is not ok or has error
      if (!response.ok || data.status >= 400) {
        throw new Error(data.message || 'Failed to fetch document');
      }

      const jsonContent = ensureJsonContentMetadata(data.jsonContent, {
        filename: doc.filename,
        salesforceType: doc.type,
      });
      const content = fixJSONDates(jsonContent?.Metadata?.results?.[0]?.content ?? []);

      const newContent = {
          ...jsonContent,
          validator_review: data.validator_review ?? (jsonContent as { validator_review?: unknown })?.validator_review,
          Metadata: {
            ...jsonContent?.Metadata,
            results: [
            {
              ...jsonContent?.Metadata?.results?.[0],
              content,
            },
          ],
          },
        }
      setSelectedDocument({
        ...doc,
        gstoreId: doc.pdfPath,
        jsonContent: newContent as JsonContent,
      });
      setPdfDetails(data);
    } catch (error) {
      console.error('Error fetching PDF:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const DUPLICATE_RECORD_MESSAGE = "Duplicate Record with status beyond 'Data Check' found.";

  const saveToSalesforce = async (document: DocumentData) => {
    const response = await fetch(`${config.api.base}${config.api.documentProcessing}`, {
      method: 'POST',
      headers: commonHeaders,
      body: JSON.stringify(document),
    });

    const result = await response.json();

    if (result.success === false) {
      if (result.message === DUPLICATE_RECORD_MESSAGE) {
          setDuplicateDocument(document);
          setShowDuplicateModal(true);
          return result;
      }
      throw new Error(result.messageAi || 'Failed to save to Salesforce');
    }

    await fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey), filterOptions);
    return result;
  };

  const changeDocumentType = async (type: string) => {
    const res = await fetch(`${config.api.base}${config.api.documentProcessing}/change-type`, {
      method: 'POST',
      headers: commonHeaders,
      body: JSON.stringify({
        documentId: selectedDocument.pdfPath,
        type,
        jsonId: selectedDocument.jsonId,
      }),
    });

    if (!res.ok) {
      throw new Error('Failed to change document type');
    } else {
      await fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey), filterOptions);
    }
  };

  const fetchUnclassifiedDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.api.base}/v1/unclassified-documents`, {
        method: 'GET',
        headers: commonHeaders,
      });
      const data = await response.json();
      setUnclassifiedDocuments(data);
    } catch (error) {
      console.error('Error fetching unclassified documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewUnclassifiedDocument = async (fileName: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${config.api.base}/v1/unclassified-documents/view`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ fileName }),
      });
      const data = await response.json();
      setUnclassifiedPdfUrl(data.url);
    } catch (error) {
      console.error('Error fetching unclassified document URL:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const classifyUnclassifiedDocument = async (fileName: string, documentType: string) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${config.api.base}/v1/unclassified-documents/classify`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ fileName, type: documentType }),
      });
      if (!response.ok) {
        throw new Error('Failed to classify document');
      }
      setUnclassifiedPdfUrl(null);
      await fetchUnclassifiedDocuments();
    } catch (error) {
      console.error('Error classifying document:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteUnclassifiedDocument = async (fileName: string) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${config.api.base}/v1/unclassified-documents`, {
        method: 'DELETE',
        headers: commonHeaders,
        body: JSON.stringify({ fileName }),
      });
      if (!response.ok) {
        throw new Error('Failed to delete unclassified document');
      }
      setUnclassifiedPdfUrl(null);
      await fetchUnclassifiedDocuments();
    } catch (error) {
      console.error('Error deleting unclassified document:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const fetchOnboardingDocuments = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${config.api.base}/v1/onboarding/documents`, {
        method: 'GET',
        headers: commonHeaders,
      });
      const data = await response.json();
      setOnboardingDocuments(data);
    } catch (error) {
      console.error('Error fetching onboarding documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewOnboardingDocument = async (fileName: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${config.api.base}/v1/onboarding/documents/view`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ fileName }),
      });
      const data = await response.json();
      setOnboardingPdfUrl(data.url);
    } catch (error) {
      console.error('Error fetching onboarding document URL:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const classifyOnboardingDocument = async (fileName: string, documentType: string) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${config.api.base}/v1/unclassified-documents/classify`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({ fileName, type: documentType, onboarding: true }),
      });
      if (!response.ok) {
        throw new Error('Failed to classify onboarding document');
      }
      setOnboardingPdfUrl(null);
      await fetchOnboardingDocuments();
    } catch (error) {
      console.error('Error classifying onboarding document:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const searchByName = async (
    query: string,
  ): Promise<{ label: string; value: string; street: string | null; city: string | null; zipCode: string | null }[]> => {
    const response = await fetch(`${config.api.base}${config.api.search}`, {
      method: 'POST',
      headers: commonHeaders,
      body: JSON.stringify({ query }),
    });
    const data = await response.json();
    const results: any[] = Array.isArray(data) ? data : (data.value || data.results || []);
    return results.map(item => {
      const label = item.name || item.Name || item.label || String(item);
      return {
          ...item,
          label,
          value: label,
          street: item.street ?? null,
          city: item.city ?? null,
          zipCode: item.zipCode ?? null,
      };
    });
  };

  const searchByFactory = async (
    query: string,
    type: string,
  ): Promise<{ label: string; value: string }[]> => {
    const response = await fetch(`${config.api.base}${config.api.search}?type=${type}`, {
      method: 'POST',
      headers: commonHeaders,
      body: JSON.stringify({ query }),
    });
    const data = await response.json();
    const results: any[] = Array.isArray(data) ? data : (data.value || data.results || []);
    return results.map(item => {
      const label = item.name || item.Name || item.label || String(item);
      return { ...item, label, value: label };
    });
  };

  const splitPreview = async (filename: string, collection?: string) => {
    const response = await fetch(`${config.api.base}${config.api.documentProcessing}/split-preview`, {
      method: 'POST',
      headers: commonHeaders,
      body: JSON.stringify({ filename, ...(collection ? { collection } : {}) }),
    });
    if (!response.ok) throw new Error('Failed to get split preview');
    return response.json();
  };

  const splitConfirm = async (gstoreId: string, splits: { pages: number[]; documentType: string }[], filename: string, collection?: string) => {
    const response = await fetch(`${config.api.base}${config.api.documentProcessing}/split-confirm`, {
      method: 'POST',
      headers: commonHeaders,
      body: JSON.stringify({ gstoreId, splits, filename, ...(collection ? { collection } : {}) }),
    });
    if (!response.ok) throw new Error('Failed to confirm split');
    return response.json();
  };

  const closeModal = () => {
    setDetailModalVisible(false);
    setSelectedDocument(null);
    setPdfDetails(null);
    setFormData({});
  };

  const handleSubmitToSalesforce = async (values: any) => {
    setIsSubmitting(true);
    const realdata = rebuildSectionsFromFormData(selectedDocument.jsonContent?.Metadata?.results[0]?.content, values);

    const formattedDateData = fixJSONDates(realdata);
    const contentForProcessed = sliceEmailBodyInContent(formattedDateData);

    const { document } = selectedDocument.jsonContent?.Metadata?.results[0] as any;
    const processedData = {
      ...selectedDocument,
      jsonId: resolveJsonIdForTab(selectedDocument.jsonId, selectedDocument.pdfPath, tabKey),
      jsonContent: {
        results: [
          {
            ...selectedDocument.jsonContent?.Metadata?.results[0],
            url: document.url,
            content: contentForProcessed,
          },
        ],
      },
      isProcessed: tabKey === '2',
    };

    try {
     const result = await saveToSalesforce(processedData as any);

     if (result.success) {
         message.success(
             intl.formatMessage({
                 id: 'document.processing.salesforce.submit.success',
                 defaultMessage: 'Successfully submitted to Salesforce',
             }),
         );
         setTabKey('1');
         closeModal();
     }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : null;
      message.error(errorMessage || intl.formatMessage({
        id: 'document.processing.salesforce.submit.failed',
        defaultMessage: 'Failed to submit to Salesforce',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitToAufzugsHandwek = async (values: any) => {
    if (!selectedDocument) {
      return;
    }

    setIsSubmitting(true);
    try {
      const realdata = rebuildSectionsFromFormData(selectedDocument.jsonContent?.Metadata?.results[0]?.content, values);
      const items = extractAssessmentReportItems(realdata);
      const zipCode = extractAssessmentReportZipCode(realdata);

      if (items.length === 0) {
        throw new Error(intl.formatMessage({
          id: 'document.processing.ai.submit.noItems',
          defaultMessage: 'No deficiencies, dangers, or observations found to submit.',
        }));
      }

      // pdfPath is always `documents/<GStoreId>` — strip the prefix to get the raw
      // GStoreId the backend uses as the Mongo lookup key for this extraction.
      // (selectedDocument.gstoreId isn't usable here: getPdfUrl overwrites it with pdfPath itself.)
      const gstoreId = selectedDocument.pdfPath?.replace(/^documents\//, '') || '';

      // When this extraction was opened from a matched portal document, we have
      // its real CustomerDocument.id — the backend resolves gcpDocumentPath/customer
      // from that directly, so gcpDocumentPath/customerEmail aren't needed at all.
      // Otherwise (a standalone, untracked extraction) fall back to the old path.
      const trackingPayload = selectedDocument.landingPageDocId
        ? { landingPageDocId: selectedDocument.landingPageDocId }
        : {
            gcpDocumentPath: selectedDocument.pdfPath,
            gstoreId,
            // TODO: standalone extraction rows have no real customer email on
            // them — hardcoded until there's a proper source for this.
            // resolveDocumentTracking requires it to find-or-create the
            // CustomerDocument that backs the case.
            customerEmail: 'shaheryar.khan@elevoniq.de',
          };

      const response = await fetch(`${config.api.base}${config.api.landingPageAssessmentReportsSubmit}`, {
        method: 'POST',
        headers: commonHeaders,
        body: JSON.stringify({
          ...trackingPayload,
          items,
          fabrikNumber: selectedDocument.fabrikNumber,
          assessmentDate: selectedDocument.documentDate,
          zipCode,
        }),
      });

      const result = await response.json();

      if (!response.ok || result.success === false) {
        throw new Error(result.messageAi || result.message || 'Failed to submit to AI');
      }

      message.success(intl.formatMessage({
        id: 'document.processing.ai.submit.success',
        defaultMessage: 'Successfully submitted to AI',
      }));
      closeModal();
      setLandingPageRefreshToken(token => token + 1);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : null;
      message.error(errorMessage || intl.formatMessage({
        id: 'document.processing.ai.submit.failed',
        defaultMessage: 'Failed to submit to AI',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const save = async (values: any) => {
    setIsSubmitting(true);
    const realdata = rebuildSectionsFromFormData(selectedDocument.jsonContent?.Metadata?.results[0]?.content, values);
console.log(JSON.stringify(realdata, null, 2), 'realdata');
    const formattedDateData = fixJSONDates(realdata);
    const contentForProcessed = sliceEmailBodyInContent(formattedDateData);

    const { document } = selectedDocument.jsonContent?.Metadata?.results[0] as any;
    const processedData = {
      ...selectedDocument,
      jsonId: resolveJsonIdForTab(selectedDocument.jsonId, selectedDocument.pdfPath, tabKey),
      jsonContent: {
        results: [
          {
            ...selectedDocument.jsonContent?.Metadata?.results[0],
            url: document.url,
            content: contentForProcessed,
          },
        ],
      },
      isProcessed: tabKey === '2',
    };

    // Call save API endpoint instead of submit endpoint
    await fetch(`${config.api.base}${config.api.documentProcessing}/save`, {
      method: 'POST',
      headers: commonHeaders,
      body: JSON.stringify(processedData),
    });

    await fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey));
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (tabKey === '1' || tabKey === '2' || tabKey === '3' || tabKey === '7' || tabKey === '8') {
      fetchDocuments(getDocumentTypeBaseOnTabKey(tabKey));
    }
  }, [tabKey]);

  const value = {
      documents,
      loading,
      selectedDocument,
      extractedData,
      commonHeaders,
      fetchDocuments,
      submitToSalesforce,
      updateExtractedData,
      setSelectedDocument,
      setExtractedData,
      getPdfUrl,
      pdfDetails,
      formData,
      setFormData,
      saveToSalesforce,
      processedDocuments,
      filteredTableData,
      setFilteredTableData,
      filterOptions,
      setFilterOptions,
      tabKey,
      setTabKey,
      handleSubmitToSalesforce,
      submitToAufzugsHandwek,
      landingPageRefreshToken,
      setLandingPageRefreshToken,
      detailModalVisible,
      setDetailModalVisible,
      closeModal,
      isSubmitting,
      handleDeleteDocument,
      showDeleteModal,
      setShowDeleteModal,
      deletedDocuments,
      revertToPending,
      submitAiSubmittedToPending,
      showRevertModal,
      setShowRevertModal,
      openSendEmailModal,
      setOpenSendEmailModal,
      emailFrom,
      setEmailFrom,
      sendEmail,
      showDocumentUploadModal,
      setShowDocumentUploadModal,
      changeDocumentType,
      getDocumentTypeBaseOnTabKey,
      pagination,
      setPagination,
      totalCount,
      save,
      unclassifiedDocuments,
      fetchUnclassifiedDocuments,
      viewUnclassifiedDocument,
      unclassifiedPdfUrl,
      setUnclassifiedPdfUrl,
      classifyUnclassifiedDocument,
      deleteUnclassifiedDocument,
      restoreDeletedToPending,
      resendForExtraction,
      showReextractModal,
      setShowReextractModal,
      onboardingDocuments,
      fetchOnboardingDocuments,
      viewOnboardingDocument,
      classifyOnboardingDocument,
      onboardingPdfUrl,
      setOnboardingPdfUrl,
      searchByName,
      searchByFactory,
      showDuplicateModal,
      setShowDuplicateModal,
      duplicateDocument,
      splitPreview,
      splitConfirm,
  };

  return <DocumentProcessingContext.Provider value={value}>{children}</DocumentProcessingContext.Provider>;
};